import { Button, Checkbox, Dialog, Loading, Notification } from "@osttra/ds-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { checkSessionStatus, checkUserSessionAndFetchApps, makeHttpCallToValidateUser } from "../../api/Service";
import { samlLoginUrl } from "../../constants/Constants";
import { useRecentEmails } from "../../hooks/useRecentEmails";
import OsttraBlack from "../../images/OSTTRA.png";
import OsttraBg from "../../images/osttra-bg.png";
import LoginQueryParams from "../../types/LoginQueryParams";
import UserEmailDetails from "../../types/UserEmailDetails";
import {
  checkForValidEmail,
  getUserLoginUrl,
  normalizeString,
  redirectToForgetPassword,
  redirectToLogin,
} from "../../utils/Utils";
import { ApplicationApiResponse } from "../app-gallery/Applications";
import LoginInput from "../common";
import LoginFormFooter from "../login-form-footer/LoginFormFooter";
import LoginInformationSection from "../login-information-section/LoginInformationSection";
import "../login/Login.css";

const SamlLogin = () => {
  const [username, setUsername] = useState("");
  const [showNonFedMessage, setShowNonFedMessage] = useState(false);
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isLoginButtonLoader, setIsLoginButtonLoader] = useState(false);
  const memberUserType: string = "Member";
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [userNotFoundFlag, setUserNotFoundFlag] = useState(false);
  const [userNotFoundMsg, setUserNotFoundMsg] = useState("User Not Found");
  const [prevEmail, setPrevEmail] = useState("");
  const [nonFedLoginMessagePromptCheckbox, setNonFedLoginMessagePromptCheckbox] = useState(false);
  const nonFedLoginMessagePrompt: boolean = !!localStorage.getItem("nonFedLoginMessagePrompt");
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const login_hint = searchParams.get("login_hint");
  const [loginParams] = useState<LoginQueryParams>(() => {
    const queryParams = new URLSearchParams(location.search);
    return {
      client_id: queryParams.get("client_id") || "",
      response_type: queryParams.get("response_type") || "",
      redirect_uri: queryParams.get("redirect_uri") || "",
      scope: queryParams.get("scope") || "",
      state: queryParams.get("state") || "",
      nonce: queryParams.get("nonce") || "",
      authRedirectUri: queryParams.get("auth_redirect_uri") || "",
    };
  });
  const [userEmailDetails, setUserEmailDetails] = useState<UserEmailDetails>({
    userLoginId: "",
    firstTimeLogin: false,
    userType: "",
  });

  const { filteredEmails, recentEmailAdd, handleRecentEmailDeleteClick } = useRecentEmails(username);

  // Use Effect for any change in username
  useEffect(() => {
    setIsValidEmail(checkForValidEmail(username));
  }, [username]);

  useEffect(() => {
    const checkSession = async () => {
      const response = await checkUserSessionAndFetchApps();
      if (response) {
        const appApiResponse: ApplicationApiResponse = response;
        navigate("/applications", { state: { appResponse: appApiResponse } });
      } else {
        const doesSessionExist = await checkSessionStatus();
        if (doesSessionExist) {
          let loginUrl = samlLoginUrl;
          if (location.search) {
            loginUrl += "?" + new URLSearchParams(location.search);
          }
          redirectToLogin(loginUrl);
        }
        setIsCheckingSession(false);
      }
    };

    const validateUser = async (login_hint: string) => {
      const encoded = encodeURIComponent(login_hint);
      const response = await makeHttpCallToValidateUser(encoded);
      if (response && response.userLoginId && !response.error) {
        if (normalizeString(login_hint) === normalizeString(response.userLoginId)) {
          const loginRequestUrl = getUserLoginUrl(samlLoginUrl, response.userLoginId, loginParams);
          redirectToLogin(loginRequestUrl);
          return;
        }
      } else {
        setUserNotFoundMsg(response?.message || "User Not Found");
        setUserNotFoundFlag(true);
      }
      setIsCheckingSession(false);
      setUsername(login_hint);
    };

    if (login_hint) {
      validateUser(login_hint);
    } else {
      checkSession();
    }
  }, [navigate, login_hint, location.search, loginParams]);

  const redirectToLoginWithTimeout = (url: string) => {
    setIsLoginButtonLoader(true);
    redirectToLogin(url);
    setTimeout(() => {
      setIsLoginButtonLoader(false);
    }, 4000);
  };

  // Function to handle login button click
  const handleLoginButtonClick = async () => {
    const encodedUsername = encodeURIComponent(username);
    if (userEmailDetails.userLoginId === "" || username !== prevEmail) {
      setPrevEmail(username);
      if (username && isValidEmail) {
        setIsLoginButtonLoader(true);
        // Making a get call to Idp API to fetch user email details
        const response = await makeHttpCallToValidateUser(encodedUsername);
        if (response && !response.error) {
          recentEmailAdd(username);
          setUserEmailDetails(response);
          handleFreshAndExistingUserResponse(encodedUsername, response);
          return;
        } else {
          setUserNotFoundMsg(response?.message || "User Not Found");
          setUserNotFoundFlag(true);
          setIsLoginButtonLoader(false);
        }
      }
    } else {
      setIsLoginButtonLoader(true);
      handleFreshAndExistingUserResponse(encodedUsername, userEmailDetails);
    }
  };

  //Function to handle redirections for fresh and existing user response
  const handleFreshAndExistingUserResponse = (email: string, userEmailDetails: UserEmailDetails) => {
    const loginUrl = getUserLoginUrl(samlLoginUrl, userEmailDetails.userLoginId, loginParams);
    if (userEmailDetails.userLoginId && memberUserType === userEmailDetails.userType) {
      if (userEmailDetails.firstTimeLogin) {
        setShowForgotPasswordDialog(true);
        setIsLoginButtonLoader(false);
      } else {
        if (nonFedLoginMessagePrompt) {
          redirectToLoginWithTimeout(loginUrl);
        } else {
          setShowNonFedMessage(true);
          setIsLoginButtonLoader(false);
        }
      }
    } else {
      redirectToLoginWithTimeout(loginUrl);
    }
  };

  // Function to redirect to login URL
  const handleRedirectToLoginNonFedDialog = (event: any) => {
    if (nonFedLoginMessagePromptCheckbox) {
      localStorage.setItem("nonFedLoginMessagePrompt", "true");
    }
    const userLoginId = userEmailDetails?.userLoginId;
    if (userLoginId) {
      const userLoginUrl = getUserLoginUrl(samlLoginUrl, userLoginId, loginParams);
      redirectToLoginWithTimeout(userLoginUrl);
    }
  };

  return (
    <div style={{ display: "block" }}>
      <div className="login-osttra-wrapper">
        <img src={OsttraBg} alt="osttra-background" className="osttra-background" />
        <main>
          {isCheckingSession ? (
            <Loading type="overlay" />
          ) : (
            <>
              <div className="form-container">
                <div className={"formContent"}>
                  <img src={OsttraBlack} alt="osttra" />
                  <form
                    className="form-login-block"
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleLoginButtonClick();
                    }}
                  >
                    <div className="sub-header">
                      Login to your account
                      <div className="t-mt-12">
                        <div className="t-mb-4">Sign in with your individual work email.</div>
                        <div>Do not use a group or distribution list (DL) address.</div>
                      </div>
                    </div>
                    <LoginInput
                      filteredEmails={filteredEmails}
                      username={username}
                      setUsername={(username) => {
                        setUsername(username);
                        setUserNotFoundFlag(false);
                      }}
                      isValidEmail={isValidEmail}
                      handleRecentEmailDeleteClick={handleRecentEmailDeleteClick}
                    />
                    <div className="login-button">
                      <Button
                        buttonType="submit"
                        onClick={handleLoginButtonClick}
                        disabled={!isValidEmail}
                        loading={isLoginButtonLoader}
                        type="primary"
                      >
                        Login
                      </Button>
                    </div>
                    <br />
                    {userNotFoundFlag && (
                      <Notification isVisible subtle type="error">
                        {userNotFoundMsg}
                      </Notification>
                    )}
                  </form>
                  <Dialog
                    ariaDescribedBy="description-id"
                    ariaLabelledBy="header-id"
                    autoFocus
                    dismissOnOutsidePress={false}
                    isOpen={showNonFedMessage && !nonFedLoginMessagePrompt}
                    lockScroll={true}
                    portalNodeId=""
                    size="medium"
                  >
                    <React.Fragment key=".0">
                      {" "}
                      <Dialog.Header id="header-id">Important Information</Dialog.Header>
                      <Dialog.Content id="description-id">
                        <p>
                          Your login uses your primary email (<b>{username}</b>). Due to our secure Single Sign-On (SSO)
                          system, a unique username to OSTTRA (e.g.,<b>{userEmailDetails?.userLoginId}</b>) may appear
                          on later screens.
                          <br />
                          <br />
                          This is expected and ensures a secure connection. Your original email is still your main
                          username, contact and will be used for all communications.
                        </p>
                      </Dialog.Content>
                      <Dialog.Footer>
                        <div>
                          <Checkbox
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNonFedLoginMessagePromptCheckbox(e.target.checked);
                              }
                            }}
                          >
                            Don't show this message again
                          </Checkbox>
                        </div>
                        <div>
                          <Button onClick={() => setShowNonFedMessage(false)}>Go Back</Button>
                          <Button type="primary" onClick={handleRedirectToLoginNonFedDialog}>
                            I understand, continue
                          </Button>
                        </div>
                      </Dialog.Footer>
                    </React.Fragment>
                  </Dialog>

                  <Dialog
                    ariaDescribedBy="description-id"
                    ariaLabelledBy="header-id"
                    autoFocus
                    dismissOnOutsidePress={false}
                    isOpen={showForgotPasswordDialog}
                    lockScroll={true}
                    portalNodeId=""
                    size="medium"
                  >
                    <React.Fragment key=".0">
                      {" "}
                      <Dialog.Header id="header-id">Important Information</Dialog.Header>
                      <Dialog.Content id="description-id">
                        <p>
                          For processing first time login to OSTTRA, we would need you to set your password.
                          <br />
                          <br />
                          Your login uses your primary email (<b>{username}</b>). Due to our secure Single Sign-On (SSO)
                          system, a unique username to OSTTRA (e.g.,<b>{userEmailDetails?.userLoginId}</b>) may appear
                          on later screens.
                          <br />
                          <br />
                          This is expected and ensures a secure connection. Your original email is still your main
                          username, contact and will be used for all communications.
                        </p>
                      </Dialog.Content>
                      <Dialog.Footer className="contentAlignRight">
                        <div>
                          <Button onClick={() => setShowForgotPasswordDialog(false)}>Go Back</Button>
                          <Button
                            type="primary"
                            onClick={() =>
                              userEmailDetails &&
                              userEmailDetails.userLoginId &&
                              redirectToForgetPassword(userEmailDetails.userLoginId)
                            }
                          >
                            Set Password
                          </Button>
                        </div>
                      </Dialog.Footer>
                    </React.Fragment>
                  </Dialog>

                  <LoginFormFooter />
                </div>
                <LoginInformationSection />
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};
export default SamlLogin;
